// (c) chaehongee. Confidential and proprietary.

import type { ReactElement } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import styles from '../Backstage.module.css'

interface AspectRatioDropdownProps {
    value: string
    disabled?: boolean
    onChange: (value: string) => void
}

const OPTIONS: Array<{ value: string; label: string; ratio: number | null }> = [
    { value: 'auto', label: 'Auto', ratio: null },
    { value: '9:16', label: '9:16', ratio: 9 / 16 },
    { value: '3:4', label: '3:4', ratio: 3 / 4 },
    { value: '2:3', label: '2:3', ratio: 2 / 3 },
    { value: '1:1', label: '1:1', ratio: 1 },
    { value: '4:3', label: '4:3', ratio: 4 / 3 },
    { value: '3:2', label: '3:2', ratio: 3 / 2 },
    { value: '16:9', label: '16:9', ratio: 16 / 9 },
]

export function AspectRatioDropdown(props: AspectRatioDropdownProps): ReactElement {
    const { value, disabled = false, onChange } = props
    const [open, setOpen] = useState(false)
    const [anchor, setAnchor] = useState<{ x: number; y: number; width: number } | null>(null)
    const buttonRef = useRef<HTMLButtonElement | null>(null)
    const menuRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (!open) return
        const handlePointerDown = (e: PointerEvent) => {
            const target = e.target as Node | null
            if (menuRef.current && target && menuRef.current.contains(target)) return
            if (buttonRef.current && target && buttonRef.current.contains(target)) return
            setOpen(false)
        }
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false)
        }
        window.addEventListener('pointerdown', handlePointerDown, { capture: true })
        window.addEventListener('keydown', handleKeyDown)
        return () => {
            window.removeEventListener('pointerdown', handlePointerDown, { capture: true } as any)
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [open])

    const openMenu = () => {
        if (disabled) return
        const rect = buttonRef.current?.getBoundingClientRect()
        if (!rect) return
        setAnchor({ x: rect.left, y: rect.top - 6, width: rect.width })
        setOpen(true)
    }

    const toggleMenu = () => {
        if (open) {
            setOpen(false)
        } else {
            openMenu()
        }
    }

    const selectedLabel =
        OPTIONS.find((opt) => opt.value === value)?.label ?? value

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                className={styles.aspectRatioTrigger}
                onClick={toggleMenu}
                disabled={disabled}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label="Aspect ratio"
            >
                <span className={styles.aspectRatioTriggerLabel}>AR</span>
                <span className={styles.aspectRatioTriggerValue}>{selectedLabel}</span>
                <ChevronDown className={styles.aspectRatioTriggerIcon} aria-hidden="true" />
            </button>
            {open && anchor && (
                <div
                    ref={menuRef}
                    className={`${styles.dropdownMenu} ${styles.dropdownMenuUp}`}
                    style={{ left: anchor.x, top: anchor.y, minWidth: anchor.width }}
                    role="menu"
                >
                    {OPTIONS.map((opt) => {
                        const isSelected = opt.value === value
                        const previewStyle =
                            opt.ratio == null
                                ? undefined
                                : {
                                    width: opt.ratio >= 1 ? 20 : Math.round(20 * opt.ratio),
                                    height: opt.ratio >= 1 ? Math.round(20 / opt.ratio) : 20,
                                }
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                className={styles.dropdownItem}
                                data-selected={isSelected}
                                onClick={() => {
                                    onChange(opt.value)
                                    setOpen(false)
                                }}
                                role="menuitemradio"
                                aria-checked={isSelected}
                            >
                                <span className={styles.dropdownItemIcon} aria-hidden="true">
                                    {opt.ratio == null ? (
                                        <span className={styles.dropdownItemAuto}>A</span>
                                    ) : (
                                        <span
                                            className={styles.dropdownItemRatio}
                                            style={previewStyle}
                                        />
                                    )}
                                </span>
                                <span className={styles.dropdownItemLabel}>{opt.label}</span>
                                {isSelected ? (
                                    <Check className={styles.dropdownItemCheck} aria-hidden="true" />
                                ) : null}
                            </button>
                        )
                    })}
                </div>
            )}
        </>
    )
}
